import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus,
  Receipt,
  Search,
  Filter,
  ChevronRight,
  CalendarRange,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Badge } from '@/components/common/Badge'
import { EmptyState, SkeletonRow } from '@/components/common/Loader'
import { useBillingStore } from '@/store/billingStore'
import { BILL_STATUS_OPTIONS } from '@/lib/constants'
import { cn, formatDate, formatRupees } from '@/lib/utils'

function BillStatusBadge({ status, balance }) {
  if (status === 'cancelled') return <Badge tone="muted">Cancelled</Badge>
  if (Number(balance) <= 0) return <Badge tone="success">Paid</Badge>
  return <Badge tone="accent">Balance due</Badge>
}

export default function BillingList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { list, loading, loadingMore, hasMore, fetchList, nextPage, query } = useBillingStore()
  const [patientInput, setPatientInput] = useState(searchParams.get('patient_id') || '')

  useEffect(() => {
    fetchList({
      patient_id: searchParams.get('patient_id') || '',
      status: searchParams.get('status') || '',
      date_from: searchParams.get('date_from') || '',
      date_to: searchParams.get('date_to') || '',
    }).catch(() => toast.error('Could not load bills'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(patch).forEach(([k, v]) => {
      if (!v) next.delete(k)
      else next.set(k, v)
    })
    setSearchParams(next, { replace: true })
  }

  const handlePatientSearch = (e) => {
    e.preventDefault()
    updateParams({ patient_id: patientInput.trim().toUpperCase() })
  }

  const totals = list.reduce(
    (acc, b) => {
      if (b.status === 'cancelled') return acc
      acc.total += Number(b.total || 0)
      acc.paid += Number(b.paid || 0)
      acc.balance += Number(b.balance || 0)
      return acc
    },
    { total: 0, paid: 0, balance: 0 },
  )

  return (
    <div className="container-page py-6 sm:py-8">
      <PageHeader
        eyebrow="Phase 7 — Billing"
        title="Bills"
        description="Service charges + medicines (auto-priced from inventory MRP)."
        actions={
          <Button
            onClick={() => navigate('/billing/new')}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            New bill
          </Button>
        }
      />

      {/* Totals strip */}
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl bg-white p-4 shadow-soft ring-1 ring-ink-100">
          <p className="label-text">Visible total</p>
          <p className="mt-1 text-xl font-bold tabular-nums text-ink-900">
            {formatRupees(totals.total)}
          </p>
        </div>
        <div className="rounded-2xl bg-white p-4 shadow-soft ring-1 ring-ink-100">
          <p className="label-text">Collected</p>
          <p className="mt-1 text-xl font-bold tabular-nums text-primary-800">
            {formatRupees(totals.paid)}
          </p>
        </div>
        <div className="rounded-2xl bg-white p-4 shadow-soft ring-1 ring-ink-100">
          <p className="label-text">Outstanding</p>
          <p
            className={cn(
              'mt-1 text-xl font-bold tabular-nums',
              totals.balance > 0 ? 'text-accent-700' : 'text-ink-900',
            )}
          >
            {formatRupees(totals.balance)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <form
        onSubmit={handlePatientSearch}
        className="mt-5 grid gap-3 rounded-2xl bg-white p-3 shadow-soft ring-1 ring-ink-100 sm:grid-cols-[1.2fr_1fr_1fr_180px_auto]"
      >
        <Input
          value={patientInput}
          onChange={(e) => setPatientInput(e.target.value)}
          placeholder="Patient ID (RD000PT…)"
          leftIcon={<Search className="h-4 w-4" />}
        />
        <Input
          type="date"
          value={searchParams.get('date_from') || ''}
          onChange={(e) => updateParams({ date_from: e.target.value })}
          leftIcon={<CalendarRange className="h-4 w-4" />}
          placeholder="From"
        />
        <Input
          type="date"
          value={searchParams.get('date_to') || ''}
          onChange={(e) => updateParams({ date_to: e.target.value })}
          leftIcon={<CalendarRange className="h-4 w-4" />}
          placeholder="To"
        />
        <select
          className="h-11 rounded-xl bg-white px-3 text-sm font-medium text-ink-800 ring-1 ring-inset ring-ink-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          value={searchParams.get('status') || ''}
          onChange={(e) => updateParams({ status: e.target.value })}
        >
          <option value="">Any status</option>
          {BILL_STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <Button type="submit" variant="secondary" leftIcon={<Filter className="h-4 w-4" />}>
          Apply
        </Button>
      </form>

      {/* Results */}
      <div className="mt-5">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} className="h-16" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="rounded-2xl bg-white shadow-soft ring-1 ring-ink-100">
            <EmptyState
              icon={<Receipt className="h-6 w-6" />}
              title="No bills found"
              description={
                Object.values(query).some(Boolean)
                  ? 'Try clearing filters or a different date range.'
                  : 'Create your first bill to start tracking revenue.'
              }
              action={
                <Button onClick={() => navigate('/billing/new')} leftIcon={<Plus className="h-4 w-4" />}>
                  New bill
                </Button>
              }
            />
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-2xl bg-white shadow-soft ring-1 ring-ink-100 lg:block">
              <table className="w-full text-sm">
                <thead className="bg-ink-50/60 text-left">
                  <tr>
                    <th className="px-4 py-3 label-text">Bill</th>
                    <th className="px-4 py-3 label-text">Patient</th>
                    <th className="px-4 py-3 label-text">Date</th>
                    <th className="px-4 py-3 label-text text-right">Service</th>
                    <th className="px-4 py-3 label-text text-right">Medicines</th>
                    <th className="px-4 py-3 label-text text-right">Total</th>
                    <th className="px-4 py-3 label-text text-right">Balance</th>
                    <th className="px-4 py-3 label-text">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {list.map((b) => (
                    <tr
                      key={b.bill_id}
                      className="cursor-pointer border-t border-ink-100 transition hover:bg-cream/60"
                      onClick={() => navigate(`/billing/${b.bill_id}`)}
                    >
                      <td className="px-4 py-3 font-mono text-[11px] text-ink-700">{b.bill_id}</td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/patients/${b.patient_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-semibold text-primary-800 hover:underline"
                        >
                          {b.patient_id}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-ink-700">{formatDate(b.bill_date)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-ink-700">
                        {formatRupees(b.service_charge)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-ink-700">
                        {formatRupees(b.medicine_charge)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-ink-900">
                        {formatRupees(b.total)}
                      </td>
                      <td
                        className={cn(
                          'px-4 py-3 text-right tabular-nums font-semibold',
                          Number(b.balance) > 0 ? 'text-accent-700' : 'text-emerald-700',
                        )}
                      >
                        {formatRupees(b.balance)}
                      </td>
                      <td className="px-4 py-3">
                        <BillStatusBadge status={b.status} balance={b.balance} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ChevronRight className="ml-auto h-4 w-4 text-ink-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-2 lg:hidden">
              {list.map((b) => (
                <Link
                  key={b.bill_id}
                  to={`/billing/${b.bill_id}`}
                  className="block rounded-2xl bg-white p-4 shadow-soft ring-1 ring-ink-100 transition hover:bg-cream/60"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-mono text-[11px] text-ink-500">{b.bill_id}</p>
                      <p className="text-sm font-semibold text-primary-800">{b.patient_id}</p>
                      <p className="mt-0.5 text-xs text-ink-500">{formatDate(b.bill_date)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold tabular-nums text-ink-900">
                        {formatRupees(b.total)}
                      </p>
                      <p
                        className={cn(
                          'text-xs tabular-nums font-semibold',
                          Number(b.balance) > 0 ? 'text-accent-700' : 'text-emerald-700',
                        )}
                      >
                        Balance {formatRupees(b.balance)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <BillStatusBadge status={b.status} balance={b.balance} />
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => nextPage().catch(() => toast.error('Could not load more'))}
                  loading={loadingMore}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
